import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  Plus,
  TrendingUp,
  Building,
  Users,
  Target,
  BarChart3,
  DollarSign,
  Calendar,
  Filter,
  Download,
  Eye,
  Edit,
  X,
} from 'lucide-react';
import api from '../services/api';

/**
 * Segment Reporting Component
 *
 * Provides flexible financial reporting across multiple dimensions:
 * - By custom reporting segments (product lines, regions, customer types, etc.)
 * - By organizational entity
 * - By business division
 * - By department
 */

// =====================================================
// Type Definitions
// =====================================================

interface ReportingSegment {
  id: string;
  code: string;
  name: string;
  description?: string;
  segmentType: 'product_line' | 'geographical' | 'customer_type' | 'business_unit' | 'custom';
  isActive: boolean;
}

interface SegmentAssignment {
  id: string;
  segmentId: string;
  entityId?: string;
  divisionId?: string;
  departmentId?: string;
  assignmentPercentage: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

interface SegmentReport {
  segmentId: string;
  segmentCode: string;
  segmentName: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  revenue: number;
  costOfGoodsSold: number;
  grossProfit: number;
  operatingExpenses: number;
  operatingIncome: number;
  netIncome: number;
  assets: number;
  liabilities: number;
  equity: number;
}

interface EntityReport {
  entityId: string;
  entityCode: string;
  entityName: string;
  currency: string;
  revenue: number;
  expenses: number;
  netIncome: number;
  assets: number;
  liabilities: number;
  equity: number;
}

type ReportView = 'segments' | 'entities' | 'divisions' | 'departments';

const SegmentReporting: React.FC = () => {
  // =====================================================
  // State Management
  // =====================================================

  const [activeView, setActiveView] = useState<ReportView>('segments');
  const [segments, setSegments] = useState<ReportingSegment[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<ReportingSegment | null>(null);
  const [segmentReports, setSegmentReports] = useState<SegmentReport[]>([]);
  const [entityReports, setEntityReports] = useState<EntityReport[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [showCreateSegmentModal, setShowCreateSegmentModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<SegmentReport | EntityReport | null>(null);

  // Date range for reports
  const [periodStart, setPeriodStart] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Form states
  const [segmentForm, setSegmentForm] = useState({
    code: '',
    name: '',
    description: '',
    segmentType: 'product_line' as const,
    isActive: true,
  });

  const [assignmentForm, setAssignmentForm] = useState({
    entityId: '',
    divisionId: '',
    departmentId: '',
    assignmentPercentage: 100,
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: '',
  });

  // =====================================================
  // Data Fetching
  // =====================================================

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/segments?activeOnly=false');
      setSegments(response.data.segments || []);
    } catch (error) {
      toast.error('Failed to fetch segments');
      console.error('Error fetching segments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSegmentReports = async () => {
    try {
      setLoading(true);
      const response = await api.get('/segments/reports/all', {
        params: { periodStart, periodEnd },
      });
      setSegmentReports(response.data.reports || []);
    } catch (error) {
      toast.error('Failed to fetch segment reports');
      console.error('Error fetching segment reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntityReports = async () => {
    try {
      setLoading(true);
      const response = await api.get('/segments/reports/by-entity', {
        params: { periodStart, periodEnd },
      });
      setEntityReports(response.data.reports || []);
    } catch (error) {
      toast.error('Failed to fetch entity reports');
      console.error('Error fetching entity reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // Event Handlers
  // =====================================================

  const handleCreateSegment = async () => {
    try {
      if (!segmentForm.code || !segmentForm.name) {
        toast.error('Please fill in all required fields');
        return;
      }

      await api.post('/segments', segmentForm);
      toast.success('Segment created successfully');
      setShowCreateSegmentModal(false);
      setSegmentForm({
        code: '',
        name: '',
        description: '',
        segmentType: 'product_line',
        isActive: true,
      });
      fetchSegments();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create segment');
      console.error('Error creating segment:', error);
    }
  };

  const handleCreateAssignment = async () => {
    try {
      if (!selectedSegment) {
        toast.error('Please select a segment');
        return;
      }

      if (!assignmentForm.entityId && !assignmentForm.divisionId && !assignmentForm.departmentId) {
        toast.error('Please select at least one organizational unit');
        return;
      }

      await api.post(`/segments/${selectedSegment.id}/assignments`, assignmentForm);
      toast.success('Assignment created successfully');
      setShowAssignmentModal(false);
      setAssignmentForm({
        entityId: '',
        divisionId: '',
        departmentId: '',
        assignmentPercentage: 100,
        effectiveFrom: new Date().toISOString().split('T')[0],
        effectiveTo: '',
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create assignment');
      console.error('Error creating assignment:', error);
    }
  };

  const handleGenerateReports = () => {
    if (activeView === 'segments') {
      fetchSegmentReports();
    } else if (activeView === 'entities') {
      fetchEntityReports();
    }
  };

  const handleViewReport = (report: SegmentReport | EntityReport) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  // =====================================================
  // Helper Functions
  // =====================================================

  const getSegmentTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      product_line: 'Product Line',
      geographical: 'Geographical',
      customer_type: 'Customer Type',
      business_unit: 'Business Unit',
      custom: 'Custom',
    };
    return labels[type] || type;
  };

  const formatCurrency = (amount: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  // =====================================================
  // Render Components
  // =====================================================

  const renderViewTabs = () => (
    <div className="flex space-x-2 mb-6 border-b border-neutral-200">
      <button
        onClick={() => setActiveView('segments')}
        className={`px-4 py-2 font-medium transition ${
          activeView === 'segments'
            ? 'text-primary-600 border-b-2 border-primary-600'
            : 'text-neutral-600 hover:text-neutral-900'
        }`}
      >
        <div className="flex items-center space-x-2">
          <Target size={18} />
          <span>Custom Segments</span>
        </div>
      </button>
      <button
        onClick={() => setActiveView('entities')}
        className={`px-4 py-2 font-medium transition ${
          activeView === 'entities'
            ? 'text-primary-600 border-b-2 border-primary-600'
            : 'text-neutral-600 hover:text-neutral-900'
        }`}
      >
        <div className="flex items-center space-x-2">
          <Building size={18} />
          <span>By Entity</span>
        </div>
      </button>
      <button
        onClick={() => setActiveView('divisions')}
        className={`px-4 py-2 font-medium transition ${
          activeView === 'divisions'
            ? 'text-primary-600 border-b-2 border-primary-600'
            : 'text-neutral-600 hover:text-neutral-900'
        }`}
      >
        <div className="flex items-center space-x-2">
          <Users size={18} />
          <span>By Division</span>
        </div>
      </button>
      <button
        onClick={() => setActiveView('departments')}
        className={`px-4 py-2 font-medium transition ${
          activeView === 'departments'
            ? 'text-primary-600 border-b-2 border-primary-600'
            : 'text-neutral-600 hover:text-neutral-900'
        }`}
      >
        <div className="flex items-center space-x-2">
          <BarChart3 size={18} />
          <span>By Department</span>
        </div>
      </button>
    </div>
  );

  const renderDateRangeSelector = () => (
    <div className="flex items-center space-x-4 mb-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
      <div className="flex items-center space-x-2">
        <Calendar size={18} className="text-neutral-600" />
        <span className="text-sm font-medium text-neutral-700">Report Period:</span>
      </div>
      <div className="flex items-center space-x-2">
        <label className="text-sm text-neutral-600">From:</label>
        <input
          type="date"
          value={periodStart}
          onChange={(e) => setPeriodStart(e.target.value)}
          className="px-3 py-1 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div className="flex items-center space-x-2">
        <label className="text-sm text-neutral-600">To:</label>
        <input
          type="date"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
          className="px-3 py-1 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <button
        onClick={handleGenerateReports}
        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center space-x-2"
      >
        <BarChart3 size={18} />
        <span>Generate Reports</span>
      </button>
    </div>
  );

  const renderSegmentsList = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-neutral-900">Reporting Segments</h2>
        <button
          onClick={() => setShowCreateSegmentModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center space-x-2"
        >
          <Plus size={18} />
          <span>Create Segment</span>
        </button>
      </div>

      {segments.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg border border-neutral-200">
          <Target size={48} className="mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600 mb-4">No reporting segments created yet</p>
          <button
            onClick={() => setShowCreateSegmentModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            Create Your First Segment
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {segments.map((segment) => (
            <div
              key={segment.id}
              className="p-4 bg-white border border-neutral-200 rounded-lg hover:shadow-lg transition cursor-pointer"
              onClick={() => setSelectedSegment(segment)}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">{segment.name}</h3>
                  <p className="text-sm text-neutral-600">{segment.code}</p>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    segment.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-neutral-100 text-neutral-600'
                  }`}
                >
                  {segment.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-sm text-neutral-600 mb-3">
                {getSegmentTypeLabel(segment.segmentType)}
              </p>
              {segment.description && (
                <p className="text-sm text-neutral-500 mb-3">{segment.description}</p>
              )}
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSegment(segment);
                    setShowAssignmentModal(true);
                  }}
                  className="flex-1 px-3 py-1 bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition text-sm"
                >
                  Assign Units
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSegmentReports = () => (
    <div className="space-y-4">
      {segmentReports.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg border border-neutral-200">
          <BarChart3 size={48} className="mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600 mb-4">No segment reports generated yet</p>
          <p className="text-sm text-neutral-500 mb-4">
            Select a date range and click "Generate Reports" to create segment-based financial
            reports
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-neutral-900">Segment Performance</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-neutral-200 rounded-lg">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                    Segment
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Revenue
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Gross Profit
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Operating Income
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Net Income
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Margin %
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {segmentReports.map((report) => {
                  const margin = report.revenue > 0 ? (report.netIncome / report.revenue) * 100 : 0;
                  return (
                    <tr key={report.segmentId} className="border-t border-neutral-200 hover:bg-neutral-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-neutral-900">{report.segmentName}</p>
                          <p className="text-xs text-neutral-600">{report.segmentCode}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-neutral-900">
                        {formatCurrency(report.revenue, report.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-neutral-900">
                        {formatCurrency(report.grossProfit, report.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-neutral-900">
                        {formatCurrency(report.operatingIncome, report.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        <span
                          className={
                            report.netIncome >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'
                          }
                        >
                          {formatCurrency(report.netIncome, report.currency)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        <span
                          className={
                            margin >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'
                          }
                        >
                          {formatPercentage(margin)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleViewReport(report)}
                          className="text-primary-600 hover:text-primary-800"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderEntityReports = () => (
    <div className="space-y-4">
      {entityReports.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg border border-neutral-200">
          <Building size={48} className="mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600 mb-4">No entity reports generated yet</p>
          <p className="text-sm text-neutral-500 mb-4">
            Select a date range and click "Generate Reports" to create entity-based financial
            reports
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-neutral-900">Entity Performance</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-neutral-200 rounded-lg">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                    Entity
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Revenue
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Expenses
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Net Income
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Assets
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">
                    Equity
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {entityReports.map((report) => (
                  <tr key={report.entityId} className="border-t border-neutral-200 hover:bg-neutral-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-neutral-900">{report.entityName}</p>
                        <p className="text-xs text-neutral-600">{report.entityCode}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-neutral-900">
                      {formatCurrency(report.revenue, report.currency)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-neutral-900">
                      {formatCurrency(report.expenses, report.currency)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      <span
                        className={
                          report.netIncome >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'
                        }
                      >
                        {formatCurrency(report.netIncome, report.currency)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-neutral-900">
                      {formatCurrency(report.assets, report.currency)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-neutral-900">
                      {formatCurrency(report.equity, report.currency)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleViewReport(report)}
                        className="text-primary-600 hover:text-primary-800"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // =====================================================
  // Modals
  // =====================================================

  const renderCreateSegmentModal = () => {
    if (!showCreateSegmentModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="flex justify-between items-center p-6 border-b border-neutral-200">
            <h3 className="text-xl font-bold text-neutral-900">Create Reporting Segment</h3>
            <button
              onClick={() => setShowCreateSegmentModal(false)}
              className="text-neutral-400 hover:text-neutral-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={segmentForm.code}
                onChange={(e) => setSegmentForm({ ...segmentForm, code: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., PROD-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={segmentForm.name}
                onChange={(e) => setSegmentForm({ ...segmentForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Enterprise Software"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Description
              </label>
              <textarea
                value={segmentForm.description}
                onChange={(e) => setSegmentForm({ ...segmentForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Optional description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Segment Type <span className="text-red-500">*</span>
              </label>
              <select
                value={segmentForm.segmentType}
                onChange={(e) =>
                  setSegmentForm({
                    ...segmentForm,
                    segmentType: e.target.value as any,
                  })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="product_line">Product Line</option>
                <option value="geographical">Geographical</option>
                <option value="customer_type">Customer Type</option>
                <option value="business_unit">Business Unit</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={segmentForm.isActive}
                onChange={(e) => setSegmentForm({ ...segmentForm, isActive: e.target.checked })}
                className="mr-2"
              />
              <label className="text-sm text-neutral-700">Active</label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 p-6 border-t border-neutral-200">
            <button
              onClick={() => setShowCreateSegmentModal(false)}
              className="px-4 py-2 bg-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-300 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateSegment}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
            >
              Create Segment
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAssignmentModal = () => {
    if (!showAssignmentModal || !selectedSegment) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="flex justify-between items-center p-6 border-b border-neutral-200">
            <h3 className="text-xl font-bold text-neutral-900">
              Assign to {selectedSegment.name}
            </h3>
            <button
              onClick={() => setShowAssignmentModal(false)}
              className="text-neutral-400 hover:text-neutral-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-sm text-neutral-600 mb-4">
              Assign entities, divisions, or departments to this segment for reporting purposes.
            </p>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Entity ID (optional)
              </label>
              <input
                type="text"
                value={assignmentForm.entityId}
                onChange={(e) =>
                  setAssignmentForm({ ...assignmentForm, entityId: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Entity UUID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Division ID (optional)
              </label>
              <input
                type="text"
                value={assignmentForm.divisionId}
                onChange={(e) =>
                  setAssignmentForm({ ...assignmentForm, divisionId: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Division UUID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Department ID (optional)
              </label>
              <input
                type="text"
                value={assignmentForm.departmentId}
                onChange={(e) =>
                  setAssignmentForm({ ...assignmentForm, departmentId: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Department UUID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Assignment Percentage
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={assignmentForm.assignmentPercentage}
                onChange={(e) =>
                  setAssignmentForm({
                    ...assignmentForm,
                    assignmentPercentage: parseFloat(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Effective From <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={assignmentForm.effectiveFrom}
                onChange={(e) =>
                  setAssignmentForm({ ...assignmentForm, effectiveFrom: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Effective To (optional)
              </label>
              <input
                type="date"
                value={assignmentForm.effectiveTo}
                onChange={(e) =>
                  setAssignmentForm({ ...assignmentForm, effectiveTo: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 p-6 border-t border-neutral-200">
            <button
              onClick={() => setShowAssignmentModal(false)}
              className="px-4 py-2 bg-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-300 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateAssignment}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
            >
              Create Assignment
            </button>
          </div>
        </div>
      </div>
    );
  };

  // =====================================================
  // Main Render
  // =====================================================

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Segment Reporting</h1>
        <p className="text-neutral-600">
          Generate flexible financial reports across multiple dimensions: custom segments,
          entities, divisions, and departments
        </p>
      </div>

      {/* View Tabs */}
      {renderViewTabs()}

      {/* Date Range Selector */}
      {renderDateRangeSelector()}

      {/* Content based on active view */}
      {activeView === 'segments' && (
        <>
          {renderSegmentsList()}
          <div className="mt-8">{renderSegmentReports()}</div>
        </>
      )}
      {activeView === 'entities' && renderEntityReports()}
      {activeView === 'divisions' && (
        <div className="text-center py-12 bg-neutral-50 rounded-lg border border-neutral-200">
          <BarChart3 size={48} className="mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600">Division reporting coming soon</p>
        </div>
      )}
      {activeView === 'departments' && (
        <div className="text-center py-12 bg-neutral-50 rounded-lg border border-neutral-200">
          <BarChart3 size={48} className="mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600">Department reporting coming soon</p>
        </div>
      )}

      {/* Modals */}
      {renderCreateSegmentModal()}
      {renderAssignmentModal()}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-neutral-700">Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentReporting;
